import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { messagingService } from '@services/messagingService';
import { chatSocket, CHAT_EVENTS } from '@services/chatSocket';
import { useAuthStore } from '@store/authStore';
import { clearInboxPartnerUnread, INBOX_QUERY_KEY } from '@utils/messagingCache';

export const UNREAD_MESSAGES_QUERY_KEY = ['messages', 'unread-count'] as const;

export function useUnreadMessages() {
  const queryClient = useQueryClient();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const role = useAuthStore((s) => s.user?.role);
  const userId = useAuthStore((s) => s.user?.id);
  const inboxDebounceRef = useRef<number | null>(null);

  const query = useQuery({
    queryKey: UNREAD_MESSAGES_QUERY_KEY,
    queryFn: () => messagingService.getUnreadCount(),
    enabled: isAuthenticated && (role === 'wholesaler' || role === 'restaurant'),
    refetchInterval: false,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (!isAuthenticated) return;

    const offUnread = chatSocket.on(CHAT_EVENTS.UNREAD_UPDATE, (payload) => {
      const data = payload as { count: number };
      queryClient.setQueryData(UNREAD_MESSAGES_QUERY_KEY, { count: data.count });
      void queryClient.invalidateQueries({ queryKey: INBOX_QUERY_KEY });
    });

    const offRead = chatSocket.on(CHAT_EVENTS.MESSAGE_READ, (payload) => {
      const { readerId, partnerId } = payload as { readerId: string; partnerId?: string };
      if (readerId === userId && partnerId) {
        clearInboxPartnerUnread(queryClient, partnerId);
      }
    });

    const offNew = chatSocket.on(CHAT_EVENTS.MESSAGE_NEW, () => {
      if (inboxDebounceRef.current) {
        window.clearTimeout(inboxDebounceRef.current);
      }
      inboxDebounceRef.current = window.setTimeout(() => {
        void queryClient.invalidateQueries({ queryKey: INBOX_QUERY_KEY });
      }, 1500);
    });

    return () => {
      offUnread();
      offRead();
      offNew();
      if (inboxDebounceRef.current) {
        window.clearTimeout(inboxDebounceRef.current);
      }
    };
  }, [isAuthenticated, queryClient, userId]);

  return query;
}

export function useChatSocketLifecycle() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const role = useAuthStore((s) => s.user?.role);

  useEffect(() => {
    const enabled =
      isAuthenticated && (role === 'wholesaler' || role === 'restaurant');
    if (!enabled) return;

    chatSocket.acquire();
    return () => chatSocket.release();
  }, [isAuthenticated, role]);
}
